/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeAutocomplete,
  BaSeButton,
  BaSeGrid,
  BaSeGridItem,
  BaSeHeading6,
  BaSeInput,
  BaSeOptionPicker,
  BaSeParagraph,
  BaSeTag,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useCityController } from '../../../../../hooks/controllers/city-controller';
import { useUfController } from '../../../../../hooks/controllers/uf-controller';
import { useMessage } from '../../../../../hooks/messages/useMessage';
import { useManageClassService } from '../../../../../hooks/services/manage-class-service';
import { useRegionalService } from '../../../../../hooks/services/regional-service';
import { Container, ContainerButton, ContentList } from './regional-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

function EditRegional({ closeModal, fetchRefional, data }) {
  const { getUfs } = useUfController();
  const { getCitiesByUf, rawCities } = useCityController();
  const { updateReginal, returnOrganizationalUnit } = useRegionalService();
  const { handleAttention, handleError, handleSuccess } = useMessage();
  const { getManagerbYiDuF } = useManageClassService();
  const { hasRole } = Sebrae.useAuth();

  const [listUfs, setListUfs] = React.useState([]);
  const [ufSelected, setUfSelected] = React.useState(
    data.uf ? [{ id: data.uf.id, label: data.uf.name }] : [],
  );
  const [ufDefault, setUfDefault] = React.useState([]);
  const [listCitiesSelected, setListCitiesSelected] = React.useState(
    (data.cities ?? []).map((city) => ({
      ...city,
      label: city.name,
    })),
  );
  const [listManagersSelected, setListManagersSelected] = React.useState(
    (data.responsibleManagers ?? []).map((manager) => ({
      ...manager,
      label: manager.name,
    })),
  );
  const [organizationalUniti, set0rganizationalUniti] = React.useState(
    data.codigoUnidadeOrganizacionalFoco
      ? [{
        codigoUnidadeOrganizacionalFoco: data.codigoUnidadeOrganizacionalFoco,
        id: data.codigoUnidadeOrganizacionalFoco,
        name: data.nameUnidadeOrganizacionalFoco,
        label: data.nameUnidadeOrganizacionalFoco,
      }]
      : [],
  );

  const [regionalName, setRegionalName] = React.useState(data.name);
  const [handleConfirmSave, setHandleConfirmSave] = React.useState(false);
  const [saveMode, setSaveMode] = React.useState('');

  const isNationalAdmin = React.useMemo(() => {
    return cache.getItem(cacheKeySebrae) === 'NA' || hasRole('ADMINISTRADOR');
  }, [hasRole]);

  const currentUf = React.useMemo(() => {
    return isNationalAdmin ? ufSelected : ufDefault;
  }, [isNationalAdmin, ufSelected, ufDefault]);

  const ufDisabled = React.useMemo(() => {
    return !isNationalAdmin;
  }, [isNationalAdmin]);


  React.useEffect(() => {
    if (data.uf) {
      getCitiesByUf({ id: data.uf.id });
    }

    const load = async () => {
      try {
        const response = await getUfs();
        setListUfs(response);

        const cachedSigla = cache.getItem(cacheKeySebrae);
        if (cachedSigla !== 'NA') {
          const ufDef = response.find((res) => res.sigla === cachedSigla);
          if (ufDef) {
            setUfDefault([ufDef]);
          }
        }
      } catch {
        setListUfs([]);
      }
    };
    load();
  }, [data.uf, getCitiesByUf, getUfs]);

  const handleAutocompleteSearchManager = React.useCallback(
    async (term) => {
      if (term.trim().length < 3) return [];
      try {
        const idUf = currentUf[0]?.id;
        if (!idUf) return [];

        const response = await getManagerbYiDuF({
          pageIndex: 0,
          pageSize: 100,
          nameSearch: term,
          idUf,
        });

        return response.items.map((item) => ({
          ...item,
          id: item.id,
          label: item.name,
        }));
      } catch {
        return [];
      }
    },
    [getManagerbYiDuF, currentUf],
  );

  const handleAutocompleteSearchOrganizationalUnit = React.useCallback(
    async (term) => {
      if (term.trim().length < 3) return [];
      try {
        const sigla = currentUf[0]?.sigla;
        if (!sigla) return [];

        const response = await returnOrganizationalUnit({
          nameSearch: term,
        }, sigla);

        return response.map((item) => ({
          ...item,
          id: item.codigoUnidadeOrganizacionalFoco,
          label: item.name,
        }));
      } catch {
        return [];
      }
    },
    [returnOrganizationalUnit, currentUf],
  );

  const handleSelectUf = (values) => {
    if (values.length === 0) return;
    getCitiesByUf({ id: values[0].id });
    setUfSelected(values);
    set0rganizationalUniti([]);
    setListCitiesSelected([])
  };

  const handleSelectChange = (values) => {
    setListCitiesSelected((prev) => {
      const newItems = values.filter(
        (val) => !prev.some((item) => item.id === val.id),
      );
      return [...prev, ...newItems];
    });
  };

  const handleRemoveTagCity = (id) => {
    setListCitiesSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const handleRemoveTagManager = (id) => {
    setListManagersSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const validateForm = React.useCallback(() => {
    const name = regionalName.trim();
    if (!name || name.length > 100) return false;
    if (!currentUf?.length) return false;
    if (!listCitiesSelected.length) return false;
    return true;
  }, [currentUf, regionalName, listCitiesSelected.length]);


  const handleSave = React.useCallback(
    async (action) => {
      try {
        if (!validateForm()) {
          handleAttention('Campo obrigatório não informado.');
          return;
        }

        setHandleConfirmSave(true);
        setSaveMode(action);
      } catch (error) {
        handleError(error.message);
      }
    },
    [validateForm, handleAttention, handleError],
  );

  const confirmSaveRegional = React.useCallback(async () => {
    try {
      if (!currentUf?.length) return;

      const dto = {
        uf: currentUf[0].id,
        cities: listCitiesSelected.map(({ id }) => id),
        name: regionalName.trim(),
        responsibleManagers: listManagersSelected.map(({ id }) => id),
        codigoUnidadeOrganizacionalFoco: organizationalUniti[0]?.codigoUnidadeOrganizacionalFoco,
        nameUnidadeOrganizacionalFoco: organizationalUniti[0]?.name,
      };

      await updateReginal(dto, data.id);
      handleSuccess('Operação realizada com sucesso!');
      closeModal(saveMode);
    } catch (error) {
      handleError(error.message);
    }
  }, [
    currentUf,
    listCitiesSelected,
    regionalName,
    listManagersSelected,
    updateReginal,
    data.id,
    handleSuccess,
    closeModal,
    saveMode,
    handleError,
    organizationalUniti,
  ]);

  if (handleConfirmSave) {
    return (
      <>
        <BaSeGrid gap={24}>
          <BaSeGridItem size={12}>
            <BaSeHeading6
              color={BaSeTheme.colors.feedbackInterface.atencao55}
              isBold={true}
            >
              Atenção!
            </BaSeHeading6>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">
              <strong>
                Tem certeza que deseja editar a regional {regionalName.trim()}?
              </strong>
            </BaSeParagraph>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <Container>
              <BaSeParagraph color="#636363">UF:</BaSeParagraph>
              <BaSeParagraph color="#8B99A7">
                {currentUf[0]?.label}
              </BaSeParagraph>
            </Container>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Cidades:</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">
                {listCitiesSelected.map((item) => item.label).join(', ')}.
              </BaSeParagraph>
            </ContentList>
          </BaSeGridItem>

          {listManagersSelected.length > 0 && (
            <BaSeGridItem size={12}>
              <BaSeParagraph color="#636363">
                Gestores vinculados:
              </BaSeParagraph>
              <ContentList>
                <BaSeParagraph color="#8B99A7">
                  {listManagersSelected.map((item) => item.label).join(', ')}
                </BaSeParagraph>
              </ContentList>
            </BaSeGridItem>
          )}

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Unidade Organizacional (FOCO):</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">
                {organizationalUniti[0]?.label ?? '—'}
              </BaSeParagraph>
            </ContentList>
          </BaSeGridItem>
        </BaSeGrid>

        <ContainerButton>
          <BaSeButton
            value="Confirmar"
            color={BaSeTheme.colors.defaultColors.black}
            onClick={confirmSaveRegional}
          />
          <BaSeButton
            value="Revisar"
            type="secondary"
            onClick={() => setHandleConfirmSave(false)}
          />
        </ContainerButton>
      </>
    );
  }

  return (
    <>
      <BaSeGrid gap={24}>
        <BaSeGridItem size={12}>
          <BaSeInput
            label="Nome da regional:"
            onChange={setRegionalName}
            value={regionalName}
            maxLength={100}
            placeholder="Nome da regional"
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          {ufDisabled ? (
            <BaSeOptionPicker
              maxMenuHeight="100px"
              isDisabled
              label="UF:"
              values={ufDefault}
              key="UF:"
              maxOptionsSelected={1}
              initialSelectedValuesId={ufDefault.map((i) => i.id)}
              emptyValueLabel="UF:"
            />
          ) : (
            <BaSeOptionPicker
              label="UF:"
              maxMenuHeight="100px"
              maxOptionsSelected={1}
              values={listUfs}
              emptyValueLabel="Selecione"
              onChange={handleSelectUf}
              initialSelectedValuesId={ufSelected.map((v) => v.id)}
            />
          )}
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Cidades:"
            maxMenuHeight="100px"
            maxOptionsSelected={1}
            values={rawCities}
            emptyValueLabel="Selecione uma ou mais cidades"
            searchPlaceholder="Selecione uma ou mais cidades"
            onChange={handleSelectChange}
            initialSelectedValuesId={listCitiesSelected.map((v) => v.id)}
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {listCitiesSelected.map((s) => (
              <BaSeTag
                key={s.id}
                color="default"
                limitedWidth={200}
                label={s.label}
                onClose={() => handleRemoveTagCity(s.id)}
                size="small"
                themeMode="light"
                type="primary"
              />
            ))}
          </div>
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            inputMode="search"
            label="Gestores vinculados:"
            minLengthToSearch={1}
            mode="list"
            onSearchTerm={handleAutocompleteSearchManager}
            onSelectedItem={(item, clear) => {
              setListManagersSelected((prevManagers) => {
                const existingIds = new Set(prevManagers.map((m) => m.id));
                if (!existingIds.has(item.id)) {
                  return [...prevManagers, item];
                }
                return prevManagers;
              });
              clear('');
            }}
            placeholder="Selecione um ou mais gestores"
            subLabel=""
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {listManagersSelected.map((s) => (
              <BaSeTag
                key={s.id}
                color="default"
                limitedWidth={200}
                label={s.label}
                onClose={() => handleRemoveTagManager(s.id)}
                size="small"
                themeMode="light"
                type="primary"
              />
            ))}
          </div>
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            inputMode="search"
            label="Unidade Organizacional (FOCO):"
            minLengthToSearch={1}
            mode="list"
            onSearchTerm={handleAutocompleteSearchOrganizationalUnit}
            onSelectedItem={(item, clear) => {
              set0rganizationalUniti([item]);
              clear('');
            }}
            placeholder="Selecione uma unidade organizacional"
            subLabel=""
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {organizationalUniti.map((s) => (
              <BaSeTag
                key={s.id}
                color="default"
                limitedWidth={300}
                label={s.label}
                onClose={() => set0rganizationalUniti([])}
                size="small"
                themeMode="light"
                type="primary"
              />
            ))}
          </div>
        </BaSeGridItem>
      </BaSeGrid>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 30,
        }}
      >
        <BaSeButton
          value="Salvar edição"
          onClick={() => handleSave('save')}
        />
        <BaSeButton
          value="Cancelar"
          color={BaSeTheme.colors.defaultColors.black}
          type="secondary"
          onClick={() => closeModal()}
        />
      </div>
    </>
  );
}

export default EditRegional;
